import { useCallback, useEffect, useState } from "react";
import { Desktop, MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";
import { Button, TextField } from "@radix-ui/themes";

import { EmptyState } from "../../components/EmptyState";
import { useAdminRead } from "../../hooks/useAdminRead";
import { readAdminData } from "../../services/adminApi";

import type { DeviceRegistryDetail } from "../../types";
import type { DeviceSearchInput } from "./index.types";

const EMPTY_INPUT: DeviceSearchInput = { accountId: "", workspaceId: "", userId: "" };
const formatDate = (value: number): string => new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(value);

export function Devices() {
  const [input, setInput] = useState(EMPTY_INPUT);
  const [selected, setSelected] = useState<DeviceSearchInput | null>(null);
  const load = useCallback(() => {
    if (!selected) throw new Error("Cihaz kaydı kimliği eksik.");
    return readAdminData<DeviceRegistryDetail>({ view: "devices", ...selected });
  }, [selected]);
  const { data, error, isLoading, refresh, reset } = useAdminRead({ load, loadOnMount: false });
  useEffect(() => { if (selected) void refresh(); }, [selected, refresh]);
  const valid = Object.values(input).every((value) => value.trim());
  const search = () => {
    if (!valid) return;
    reset();
    setSelected({ accountId: input.accountId.trim(), workspaceId: input.workspaceId.trim(), userId: input.userId.trim() });
  };
  return (
    <main className="page">
      <section className="page-heading"><div><span className="breadcrumb">HESAPLAR / ÇEVRİMDIŞI ERİŞİM</span><h1>Cihaz kayıtları</h1><p>Yalnız hedef hesap, workspace ve kullanıcı üçlüsüne ait tek cihaz registry belgesi okunur.</p></div></section>
      <section className="search-panel multi-search">
        {(["accountId", "workspaceId", "userId"] as const).map((key) => <label key={key}><span>{key === "accountId" ? "Account ID" : key === "workspaceId" ? "Workspace ID" : "User ID"}</span><TextField.Root value={input[key]} onChange={(event) => setInput((current) => ({ ...current, [key]: event.target.value }))} /></label>)}
        <Button disabled={!valid || isLoading} onClick={search} type="button"><MagnifyingGlass size={17} />{isLoading ? "Yükleniyor…" : "Registry'yi aç"}</Button>
      </section>
      {error ? <div className="alert danger"><WarningCircle size={21} /><span><strong>Cihaz kaydı yüklenemedi</strong>{error}</span></div> : null}
      {!data ? <EmptyState icon={<Desktop size={38} />} title="Bir cihaz registry'si açın" description="Public key ve fingerprint gibi hassas alanlar yönetim yanıtına dahil edilmez." /> : (
        <section className="panel"><div className="panel-heading"><div><span className="kicker">{data.workspaceId}</span><h2>{data.devices.length} kayıtlı cihaz</h2></div><code>{data.userId}</code></div><div className="device-grid">{data.devices.map((device) => <article className="device-card" key={device.deviceId}><div><Desktop size={24} /><span className={`pill ${device.status}`}>{device.status}</span></div><h3>{device.deviceId}</h3><p>{device.appId}</p><dl><div><dt>Kanıt oluşturma</dt><dd>{formatDate(device.proofIssuedAt)}</dd></div><div><dt>Kayıt bitişi</dt><dd>{formatDate(device.registrationExpiresAt)}</dd></div></dl></article>)}</div></section>
      )}
    </main>
  );
}
